import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, X, Mic } from 'lucide-react';
import { useAudio } from '../../context/AudioContext';
import { mediaUrl, welcomeEvent } from '../../lib/programsApi';

const SERIF = { fontFamily: "'Playfair Display', serif" };

export const ProgramWelcome = ({ program, welcome, onBegin, replay = false }) => {
  const { play, pause, resume, state } = useAudio();
  const [finished, setFinished] = useState(false);
  const startedRef = useRef(false);
  const isCurrent = state.current?.contentId === welcome?.id;
  const playing = isCurrent && state.playing;

  useEffect(() => {
    const onEnded = (e) => { if (e.detail?.contentId === welcome?.id) { setFinished(true); welcomeEvent(program.slug, 'completed'); } };
    window.addEventListener('rbr:audio-ended', onEnded);
    return () => window.removeEventListener('rbr:audio-ended', onEnded);
  }, [welcome?.id, program.slug]);

  const toggle = () => {
    if (!welcome?.audioUrl) return;
    if (playing) return pause();
    if (isCurrent) return resume();
    if (!startedRef.current) { startedRef.current = true; welcomeEvent(program.slug, 'started'); }
    play({ src: mediaUrl(welcome.audioUrl), title: welcome.title, subtitle: 'A moment from Rowdy', artwork: mediaUrl(program.coverImageUrl, 800), contentId: welcome.id, kind: 'audio', sourceContext: 'program_welcome' });
  };
  const close = (skipped) => {
    if (playing) pause();
    if (skipped && !finished) welcomeEvent(program.slug, 'skipped');
    onBegin();
  };

  return createPortal(
    <div className="fixed inset-0 z-[210] bg-[#0f1e33]/95 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in" data-testid="program-welcome-screen">
      <div className="w-full max-w-sm text-center text-[#f9f4ea]">
        <button type="button" onClick={() => close(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" aria-label="Close" data-testid="program-welcome-close"><X className="w-5 h-5" /></button>
        <div className="mx-auto w-28 h-28 rounded-full overflow-hidden border-2 border-[#D6AD60] shadow-[0_0_50px_-10px_rgba(214,173,96,.6)] mb-5">
          {program.coverImageUrl ? <img src={mediaUrl(program.coverImageUrl, 800)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#1e3a5f]" />}
        </div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#e3d2a8] mb-2 inline-flex items-center gap-2"><Mic className="w-3.5 h-3.5" /> A Moment From Rowdy</p>
        <h2 className="text-2xl font-bold leading-tight mb-2" style={SERIF} data-testid="program-welcome-title">{welcome?.title || `Welcome to ${program.title}`}</h2>
        <p className="text-sm text-[#e3d2a8]/80 mb-6">A short, personal welcome before {program.dayLabel || 'Day'} 1. Optional — skip anytime.</p>
        <button type="button" onClick={toggle} disabled={!welcome?.audioUrl} className="w-full h-12 rounded-full bg-[#D6AD60] text-[#0f1e33] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 mb-3" data-testid="program-welcome-play">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? 'Pause' : finished ? 'Play again' : `Welcome to ${program.title}`}
        </button>
        <button type="button" onClick={() => close(false)} className="w-full h-12 rounded-full bg-[#f9f4ea] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] mb-2" data-testid="program-welcome-begin">{replay ? 'Back to my journey' : `Begin ${program.dayLabel || 'Day'} 1`}</button>
        {!replay && <button type="button" onClick={() => close(true)} className="text-xs text-[#e3d2a8]/80 py-2" data-testid="program-welcome-skip">Skip Welcome</button>}
      </div>
    </div>,
    document.body
  );
};
